import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Field, PrimaryButton, inputClass } from '@/components/Card';
import { Chip, ChipRow } from '@/components/ChipRow';
import { api } from '@/lib/api';

const AIRPORTS = ['EWR', 'PBI', 'RSW', 'BNA'];

export function NewTripPage() {
  const navigate = useNavigate();
  const [originIata, setOrigin] = useState('EWR');
  const [destIata, setDest] = useState('PBI');
  const [airline, setAirline] = useState('UA');
  const [cabin, setCabin] = useState('economy');
  const [arriveEarlyMinutes, setEarly] = useState(90);
  const [focus, setFocus] = useState('departure');
  const [highFriction, setFriction] = useState(false);
  const [flightNumber, setFlight] = useState('');
  const [departAt, setDepart] = useState('');
  const [label, setLabel] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const data = await api.createTrip({
        originIata,
        destIata,
        airline,
        cabin,
        arriveEarlyMinutes,
        focus,
        highFriction,
        flightNumber: flightNumber || null,
        departAt: departAt || null,
        label: label || null,
      });
      navigate(`/trips/${data.trip.id}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not create trip.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <h2 className="font-display text-[30px] leading-none text-navy">New trip</h2>
        <p className="text-sm text-muted">
          Flight number is a label only — the flight adapter is not wired. Recommendations come from the seeded catalog plus your wallet.
        </p>
        <ChipRow>
          <Chip tone="navy">{airline}</Chip>
          <Chip>{originIata}</Chip>
          <Chip>→</Chip>
          <Chip>{destIata}</Chip>
          <Chip tone="brass">{arriveEarlyMinutes} min early</Chip>
        </ChipRow>
        <Field label="Label">
          <input className={inputClass} value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Palm Beach long weekend" />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Origin">
            <select className={inputClass} value={originIata} onChange={(e) => setOrigin(e.target.value)}>
              {AIRPORTS.map((code) => (
                <option key={code}>{code}</option>
              ))}
            </select>
          </Field>
          <Field label="Destination">
            <select className={inputClass} value={destIata} onChange={(e) => setDest(e.target.value)}>
              {AIRPORTS.map((code) => (
                <option key={code}>{code}</option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Airline">
            <input className={inputClass} value={airline} onChange={(e) => setAirline(e.target.value)} />
          </Field>
          <Field label="Cabin">
            <select className={inputClass} value={cabin} onChange={(e) => setCabin(e.target.value)}>
              <option value="economy">Economy</option>
              <option value="premium">Premium</option>
              <option value="business">Business</option>
              <option value="polaris">Polaris</option>
            </select>
          </Field>
        </div>
        <Field label="Arrive early (minutes at origin)">
          <input
            className={inputClass}
            type="number"
            min={20}
            max={360}
            value={arriveEarlyMinutes}
            onChange={(e) => setEarly(Number(e.target.value))}
          />
        </Field>
        <Field label="Focus">
          <select className={inputClass} value={focus} onChange={(e) => setFocus(e.target.value)}>
            <option value="departure">Departure airport</option>
            <option value="arrival">Arrival airport</option>
            <option value="both">Both ends</option>
          </select>
        </Field>
        <Field label="Flight number (optional, not live)">
          <input className={inputClass} value={flightNumber} onChange={(e) => setFlight(e.target.value)} placeholder="UA1234" />
        </Field>
        <Field label="Depart (optional)">
          <input className={inputClass} type="datetime-local" value={departAt} onChange={(e) => setDepart(e.target.value)} />
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={highFriction} onChange={(e) => setFriction(e.target.checked)} />
          High-friction trip (mobility, family, messy connection)
        </label>
        <PrimaryButton disabled={busy} onClick={submit}>
          Build recommendations
        </PrimaryButton>
        {error && <p className="text-sm text-alert">{error}</p>}
      </Card>
    </div>
  );
}
